import {useIsAcceptingFiles} from '@github-ui/copilot-chat-app-next/NewSessionFileAcceptance'
import type {NewSessionInitialInput, NewSessionTarget} from '@github-ui/one-copilot-composer/new'
import {resolveMcpPermissionGate, type TaskComposerProps} from '@github-ui/one-copilot-composer/shared'
import type {ComposerHandle} from '@github-ui/one-copilot-composer/existing'
import {useLayoutEffect, type Ref} from 'react'

import type {EarlyTaskProvisioning} from '../../ahp/early-task-provisioning'
import {RewrittenNewSessionComposer} from './RewrittenNewSessionComposer'

function targetOf({
  initialRepo,
  initialRepositoryNameWithOwner,
  initialBranch,
  hideRepoSelector,
  initialSpace,
}: TaskComposerProps): NewSessionTarget {
  if (initialRepo) {
    return {
      kind: 'task',
      repository: {kind: 'repository', repository: initialRepo, ...(initialBranch ? {branch: initialBranch} : {})},
      lockRepository: hideRepoSelector,
    }
  }
  if (initialRepositoryNameWithOwner) {
    return {
      kind: 'task',
      repository: {
        kind: 'name',
        nameWithOwner: initialRepositoryNameWithOwner,
        ...(initialBranch ? {branch: initialBranch} : {}),
      },
      lockRepository: hideRepoSelector,
    }
  }

  return initialSpace ? {kind: 'chat', context: {kind: 'space', space: initialSpace}} : {kind: 'task'}
}

function initialInputOf(props: TaskComposerProps): NewSessionInitialInput {
  return {
    target: targetOf(props),
    behavior: 'prefill',
    ...(props.initialText ? {prompt: {text: props.initialText}} : {}),
  }
}

function FileAcceptanceReport({onFileAcceptanceChange}: {onFileAcceptanceChange: (enabled: boolean) => void}) {
  const isAcceptingFiles = useIsAcceptingFiles()

  useLayoutEffect(() => {
    onFileAcceptanceChange(isAcceptingFiles)
    return () => onFileAcceptanceChange(false)
  }, [onFileAcceptanceChange, isAcceptingFiles])

  return null
}

export type RewrittenTaskComposerProps = TaskComposerProps & {
  earlyProvisioning?: EarlyTaskProvisioning
  handleRef?: Ref<ComposerHandle>
  onFileAcceptanceChange?: (enabled: boolean) => void
  isDraggedOver?: boolean
}

/** The rewritten composer behind `copilot_chat_surfaces_rewrite`, driven by the legacy `TaskComposer` props. */
export function RewrittenTaskComposer(props: RewrittenTaskComposerProps) {
  return (
    <RewrittenNewSessionComposer
      controller={{
        createTask: props.handleUserSubmit,
        createEnvironmentTask: props.onEnvironmentSubmit,
        chatLaunch: props.chatLaunch?.kind === 'environment' ? {kind: 'environment'} : undefined,
        earlyProvisioning: props.earlyProvisioning,
      }}
      configuration={{
        supportsManagedSandbox: props.supportsManagedSandbox,
        agentQuota: props.agentQuotaSnapshot,
        mcpPermissionGateEnabled: props.mcpPermissionGateEnabled,
        enterpriseManagedSettings: props.enterpriseManagedSettings,
        enterpriseManagedSettingsUrl: props.enterpriseManagedSettingsUrl,
        disableSandboxSubmission: props.disableSandboxSubmission,
        currentUserLogin: props.currentUserLogin,
        currentUserAvatarUrl: props.currentUserAvatarUrl,
        availableOrganizations: props.availableOrganizations,
        directTurnTelemetrySurface: props.directTurnTelemetrySurface,
      }}
      initialInput={initialInputOf(props)}
      isNewConversation={props.isNewConversation}
      draftSource={props.eventType ?? ''}
      modePicker={
        props.modePicker !== undefined
          ? 'agent'
          : resolveMcpPermissionGate(props.mcpPermissionGateEnabled)
            ? 'mcp-permission'
            : 'none'
      }
      isOwnerScoped
      // Matches legacy TaskComposer: it always reopens the user's last-picked environment.
      environmentStart="restore"
      isDraggedOver={props.isDraggedOver}
      codeReference={props.initialReference ?? undefined}
      chatPlaceholder="Send a message"
      taskPlaceholder="Give Copilot a background task to work on"
      textAreaRef={props.textAreaRef}
      chatInputRef={props.chatInputRef}
      handleRef={props.handleRef}
    >
      {props.onFileAcceptanceChange ? (
        <FileAcceptanceReport onFileAcceptanceChange={props.onFileAcceptanceChange} />
      ) : null}
    </RewrittenNewSessionComposer>
  )
}
